import api from './api';

/**
 * DEM terrain (docs/DEM_TERRAIN.md): REST calls and pure helpers for the DEM panel,
 * the map's DEM products, the elevation profile and the section DEM surface.
 * A DEM is a Raster dataset with metadata.raster.kind === 'dem'.
 */

export const DEM_METHOD = 'DEM';
export const DEM_PRODUCTS = ['elevation', 'hillshade', 'slope', 'aspect', 'contours'];
export const DEM_PRODUCT_LABELS = {
  elevation: 'Elevation', hillshade: 'Hillshade', slope: 'Slope', aspect: 'Aspect', contours: 'Contours',
};
export const ELEVATION_UNITS = [['m', 'metres'], ['ft', 'feet'], ['us-ft', 'US survey feet']];
export const DEM_PROFILE_SERIES = 'Elevation (DEM)';
export const MAX_SAMPLE_POINTS = 50000;

export const terrainApi = {
  setDem: (datasetId, body) => api.put(`/api/datasets/${datasetId}/dem`, body),
  sample: (datasetId, points, spacingM) =>
    api.post(`/api/datasets/${datasetId}/dem/sample`, { points, ...(spacingM ? { spacing_m: spacingM } : {}) }),
  profile: (datasetId, body) => api.post(`/api/datasets/${datasetId}/dem/profile`, body),
  analyze: (datasetId, body) => api.post(`/api/datasets/${datasetId}/dem/analyze`, body),
  contours: (datasetId) => api.get(`/api/datasets/${datasetId}/dem/contours`),
  projectTerrain: (projectId) => api.get(`/api/projects/${projectId}/terrain`),
  setProjectTerrain: (projectId, demDatasetId) =>
    api.put(`/api/projects/${projectId}/terrain`, { dem_dataset_id: demDatasetId || null }),
  elevate: (datasetId, body = {}) => api.post(`/api/datasets/${datasetId}/georef/elevate`, body),
};

// ── Pure helpers ────────────────────────────────────────────────────────────

/** True for a processed DEM (metadata.raster.kind === 'dem'). */
export const isDem = (metadata) => metadata?.raster?.kind === 'dem' && !!metadata?.raster?.cog_path;

/** A dataset the DEM panel applies to: a DEM, the DEM method, or a single-band raster. */
export function canBeDem(ds) {
  const r = ds?.metadata?.raster;
  return ds?.method_type === DEM_METHOD || isDem(ds?.metadata) || (!!r && r.count === 1);
}

/** Products available on the map for a DEM dataset. */
export function availableDemProducts(metadata) {
  if (!isDem(metadata)) return [];
  const t = metadata.terrain || {};
  return DEM_PRODUCTS.filter((p) => p === 'elevation'
    || (p === 'hillshade' && (t.hillshade?.path || metadata.raster.dem?.hillshade_path))
    || t[p]?.path);
}

/** State of the last terrain_analysis job from metadata.terrain_job. */
export function terrainJobState(metadata) {
  const j = metadata?.terrain_job;
  if (!j) return { state: 'none' };
  if (j.status === 'queued' || j.status === 'running') return { state: 'processing', jobId: j.id };
  if (j.status === 'failed') return { state: 'failed', error: j.error || 'Terrain analysis failed' };
  return { state: j.status === 'succeeded' ? 'ready' : j.status };
}

/** Rows for the DEM block of the RasterCard. */
export function demInfoRows(raster) {
  const d = raster?.dem;
  if (!d) return [];
  const unit = d.elevation_units || 'm';
  const f = (v) => (Number.isFinite(Number(v)) && v !== null ? `${Number(v).toFixed(2)} ${unit}` : '—');
  return [
    ['Elevation units', `${unit}${d.units_source && d.units_source !== 'user' ? ` (${d.units_source.replace('_', ' ')})` : ''}`],
    ['Vertical datum', d.vertical_datum || 'Unknown'],
    ['Lowest', f(d.min)],
    ['Highest', f(d.max)],
  ];
}

/** A 1/2/5 x 10^k contour interval giving about `target` levels (mirrors terrain_api.nice_interval). */
export function niceInterval(span, target = 20) {
  if (!(span > 0)) return 1;
  const raw = span / target;
  const mag = 10 ** Math.floor(Math.log10(raw));
  for (const m of [1, 2, 5, 10]) if (raw <= m * mag + 1e-12) return m * mag;
  return 10 * mag;
}

/**
 * Merge a DEM profile (POST /dem/profile) into a grid profile computed on the same line
 * and step: both use grid_profile's path sampling, so samples align index by index.
 * Either argument may be null.
 */
export function mergeDemProfile(gridProfile, demProfile) {
  if (!demProfile) return gridProfile;
  if (!gridProfile) return { ...demProfile, soundings: demProfile.soundings || [] };
  const name = demProfile.series[0] || DEM_PROFILE_SERIES;
  const samples = gridProfile.samples.map((s, i) => ({
    ...s, values: { ...s.values, [name]: demProfile.samples[i]?.values?.[name] ?? null },
  }));
  return { ...gridProfile, series: [...gridProfile.series, name], samples };
}

/**
 * Hang a model without topography from the DEM: every cell gets `surface` = DEM elevation
 * at its x (linear interpolation in the profile, [[x, z]...]) and `top`/`bottom` from its
 * depth level, so SectionView's topography drawing applies. Cells that already carry a
 * surface (the model's own topography) are returned unchanged.
 */
export function applyDemSurface(cells, demSurface) {
  if (!Array.isArray(cells) || !cells.length || !Array.isArray(demSurface) || demSurface.length < 1) return cells;
  if (cells.some((c) => typeof c.surface === 'number')) return cells;
  const pts = [...demSurface].filter((p) => Number.isFinite(p[1])).sort((a, b) => a[0] - b[0]);
  if (!pts.length) return cells;
  const at = (x) => interpolate(pts, x);
  const depths = [...new Set(cells.map((c) => c.depth))].sort((a, b) => a - b);
  const next = new Map(depths.map((d, i) => [d, i + 1 < depths.length ? depths[i + 1] : d + (i > 0 ? d - depths[i - 1] : 1)]));
  return cells.map((c) => {
    const z = at(c.x);
    if (z === null) return c;
    return {
      ...c,
      surface: z,
      top: c.top !== undefined ? c.top : c.depth,
      bottom: c.bottom !== undefined ? c.bottom : next.get(c.depth),
      demSurface: true,
    };
  });
}

/** Linear interpolation in sorted [[x, y]...]; clamps to the ends; null when empty. */
export function interpolate(pts, x) {
  if (!pts.length) return null;
  if (x <= pts[0][0]) return pts[0][1];
  if (x >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
  let lo = 0, hi = pts.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (pts[mid][0] <= x) lo = mid; else hi = mid;
  }
  const [x0, y0] = pts[lo], [x1, y1] = pts[hi];
  return x1 === x0 ? y0 : y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
}

/**
 * DEM profile along a model line -> [[x, z]...] in the model's x: `line` is the
 * WGS84 [start, end] of `xRange`, the profile's distances are mapped by fraction of
 * the profile length (the georef scale cancels out).
 */
export function profileToSurface(profile, xRange) {
  if (!profile?.samples?.length || !Array.isArray(xRange)) return [];
  const name = profile.series?.[0] || DEM_PROFILE_SERIES;
  const L = profile.length_m || profile.samples[profile.samples.length - 1].dist || 1;
  const [x0, x1] = xRange;
  return profile.samples
    .filter((s) => s.values?.[name] !== null && s.values?.[name] !== undefined)
    .map((s) => [x0 + (s.dist / L) * (x1 - x0), s.values[name]]);
}
